import {
  useRef, useState, useEffect,
  forwardRef,
} from 'react';
import { nanoid } from 'nanoid';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import Emitter from '@/helpers/emitter.js';
import FilterPlus from './filters/filterPlus';
import FiltersContent from './filters/content';
import { useZusHomeStore } from '@/helpers/context';
import StartTourEvents from '@/components/search/events/startTour';
import NoSSR from '@/helpers/nossr';

const FiltersOnScreen = forwardRef(({ filters, keyFilter }, ref) => {
  const maxTour = Math.max(...filters.filter((o) => o.startTour?.order)
    .map((o) => o.startTour.order));
  const { isTour } = useSearchZustandStore.getState();
  // console.log('search/results/filters - ONSCREEN');
  const refStartTour = useRef();
  useEffect(() => {
    if (isTour) {
      refStartTour?.current?.runStartTour?.();
    }
  }, [isTour]);
  return (
    <div className="hidden overflow-hidden max-h-[38px] w-full gap-x-3 gap-y-4 lg:flex lg:flex-auto lg:flex-wrap">
      <FiltersContent {...{ filters, onScreen: true }} ref={ref} keyFilter={keyFilter} />
      <StartTourEvents ref={refStartTour} maxTour={maxTour} />
    </div>
  );
});

function Filters({ filters, className }) {
  // const initFilters = useSearchZustandStore((state) => state.initFilters);
  const [keyFilter] = useState(nanoid());
  // WEA-157 — gate de visibilité des filtres, réactif sur le store user (le filtre apparaît/disparaît
  // dès que userData arrive) :
  //   requireActiveSubscription -> seulement pour un abonné Legend+ actif ;
  //   requireAuth               -> seulement pour un connecté (cache aux visiteurs) ;
  //   hideForSubscriber         -> caché pour un abonné Legend+ actif (ex. filtre "HOF -X%" de palier).
  const userData = useZusUserConnectedStore((state) => state.userData);
  const isAuthenticated = useZusUserConnectedStore((state) => state.isAuthenticated);
  const isLegendEligible = !!isAuthenticated
    && ['active', 'trialing'].includes(userData?.corpo?.subscription?.status);
  const gate = (list) => (list || []).filter((f) => {
    if (f.requireActiveSubscription && !isLegendEligible) return false;
    if (f.requireAuth && !isAuthenticated) return false;
    if (f.hideForSubscriber && isLegendEligible) return false;
    return true;
  });
  const refFormOnScreen = useRef();
  const refFormOnFilter = useRef();
  const [readytocallsearch, setReadyToCallSearch] = useState();

  const [currentFilters, setCurrentFilters] = useState({
    onScreen: filters.onScreen,
    onFilter: filters.onFilter,
  });

  const [originalFilters] = useState({
    onScreen: filters.onScreen,
    onFilter: filters.onFilter,
  });

  const startingSearch = () => {
    setReadyToCallSearch(nanoid());
  };

  const consumerkeyFilterrunsearch = () => {
    useSearchZustandStore.setState({ currentPage: 1 });
    startingSearch();
  };

  const runSearch = () => {
    Emitter.emit('filterpluslistenercounting', [...refFormOnFilter?.current?.getOutput() || []]);

    // WEA-157 : les filtres HOF ("HOF -X%" / "Legend +") sont désormais de vrais items de la barre
    // (prodConf onScreen), agrégés par content.js comme n'importe quel filtre — plus d'injection
    // manuelle de la sortie du panneau (supprimé).
    useSearchZustandStore.setState({
      filterEvent: {
        action: 'search filter filter event',
        data: [
          ...(refFormOnScreen?.current?.getOutput() || []),
          ...(refFormOnFilter?.current?.getOutput() || []),
        ],
      },
    });
  };

  useEffect(() => {
    Emitter.on(`${keyFilter}runSearch`, consumerkeyFilterrunsearch);
    startingSearch();
    return () => {
      Emitter.off(`${keyFilter}runSearch`, consumerkeyFilterrunsearch);
    };
  }, [currentFilters]);

  useEffect(() => {
    if (readytocallsearch) {
      runSearch();
    }
  }, [readytocallsearch]);

  return (
    <div className={`${className}`}>
      {!currentFilters.onScreen || <FiltersOnScreen filters={gate(currentFilters.onScreen)} ref={refFormOnScreen} keyFilter={keyFilter} key={`screen${currentFilters.keyComponent}${isLegendEligible}${isAuthenticated}`} />}
      <FilterPlus filters={gate(currentFilters.onFilter)} ref={refFormOnFilter} keyFilter={keyFilter} key={`filters${currentFilters.keyComponent}${isLegendEligible}${isAuthenticated}`} />
    </div>
  );
}

function FragmentFilters({ filters, ...props }) {
  return <Filters {...{ filters }} {...props} />;
}

function FiltersContainer({ filters }) {
  const { ua } = useZusHomeStore.getState();
  const [currentCategory] = useState('production');

  return (
    <>
      {!!ua.isMobile || <FragmentFilters filters={filters[currentCategory]} className="flex overflow-hidden no-wrap" />}
      {!ua.isMobile || <FragmentFilters filters={filters[`${currentCategory}Responsive`]} className="lg:hidden" />}
    </>
  );
}

export default NoSSR(FiltersContainer);
